import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Button
} from '@mui/material';
import {
  Security,
  Lock,
  Person,
  ContactSupport,
  ArrowBack,
  Notifications,
  Storage
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { usePageSeo } from './usePageSeo';

const WhiteNoisePrivacyPolicy = () => {
  const navigate = useNavigate();
  usePageSeo({
    title: 'Privacy Policy | White Noise & Brown Noise',
    description: 'Privacy policy for White Noise & Brown Noise, a free iPhone sleep sounds app that collects no data and makes no network requests.',
    path: '/white-noise-privacy-policy'
  });

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Privacy Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Effective Date: September 27, 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            App Name: White Noise &amp; Brown Noise
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Platform: iOS
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Developer: Michael R. Ellis
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Security sx={{ mr: 1 }} />
              Overview
            </Typography>
            <Typography variant="body1" paragraph>
              White Noise &amp; Brown Noise is a soundscape app for sleep, focus, and relaxation. Every sound ships inside the App, and your mixes and settings stay on your device. We do not operate user accounts, we collect no personal information, and we do not sell or share data for advertising. The App is free and contains no subscriptions or in-app purchases.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Lock sx={{ mr: 1 }} />
              Information We Do Not Collect
            </Typography>
            <Typography variant="body1" paragraph>
              There is no account registration or login with us. We do not receive your payment card number, Apple ID password, or billing address. We do not use advertising networks or tracking technologies that identify you across other companies&apos; apps or websites.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Storage sx={{ mr: 1 }} />
              Information Stored on Your Device
            </Typography>
            <Typography variant="body1" paragraph>
              To provide core features, the app may store the following locally on your device (and, where needed for widgets or Live Activities, in an App Group shared only with this app&apos;s extensions on the same device):
            </Typography>
            <Typography component="ul" variant="body1" sx={{ pl: 3 }}>
              <li>Playback preferences (volume, sleep timer settings, audio routing options, display options)</li>
              <li>Saved mixes, recently played sounds, and custom tone settings</li>
              <li>Optional bedtime reminder time and enabled state</li>
              <li>Temporary playback and sleep-timer state used by widgets and Live Activities</li>
            </Typography>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              This information stays on your device. Uninstalling the app removes app data stored by White Noise &amp; Brown Noise, subject to how iOS manages app deletion.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Notifications sx={{ mr: 1 }} />
              Notifications &amp; Device Features
            </Typography>
            <Typography variant="body1" paragraph>
              If you enable the bedtime reminder, the app requests permission to schedule local notifications on your device. These notifications are delivered by Apple&apos;s system notification service and are not used to track you. The app may also use background audio, widgets, Live Activities, and AirPlay routing so playback can continue and be controlled as you expect. These features process data on-device and through Apple system frameworks.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              No Network Requests
            </Typography>
            <Typography variant="body1" paragraph>
              The App makes no network requests of its own. Every sound, scene, and animation is bundled inside the App itself, so nothing is streamed or downloaded, and no server of ours receives your IP address or any other connection data. The App works fully offline.
            </Typography>
            <Typography variant="body1" paragraph>
              The only time your device leaves the App is when you tap a link we show you, such as these policies, a sound contributor credit, or the App Store review page. Those open in your browser or in the App Store, and the destination&apos;s own privacy practices apply.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 1 }} />
              Third-Party Links &amp; Content
            </Typography>
            <Typography variant="body1" paragraph>
              The app may display credits or links to third-party websites (for example, sound contributor pages or the developer website). If you follow those links, their privacy practices apply, not this policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Children&apos;s Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              White Noise &amp; Brown Noise does not knowingly collect personal information from children. The app is not directed at children under 13, and it does not require personal data to use its features.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Your Choices &amp; Rights
            </Typography>
            <Typography variant="body1" paragraph>
              You can change or clear mixes and preferences in the App, turn off bedtime reminders, revoke notification permission in iOS Settings, or uninstall the App to remove local app data. Because everything stays on your device and we hold no records about you, there is nothing for us to delete on request. If you have a privacy question, email{' '}
              <a href="mailto:Contact@MichaelRobertEllis.com" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Contact@MichaelRobertEllis.com
              </a>
              {' '}and we will answer it.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. The effective date at the top of this page will be revised when changes are posted. Continued use of the app after an update constitutes acceptance of the revised policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Related Terms
            </Typography>
            <Typography variant="body1" paragraph>
              Use of White Noise &amp; Brown Noise is also governed by our{' '}
              <RouterLink to="/white-noise-terms" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Terms and Conditions
              </RouterLink>
              .
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactSupport sx={{ mr: 1 }} />
              Contact
            </Typography>
            <Typography variant="body1" paragraph>
              For privacy questions about White Noise &amp; Brown Noise, contact:{' '}
              <a href="mailto:Contact@MichaelRobertEllis.com" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Contact@MichaelRobertEllis.com
              </a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default WhiteNoisePrivacyPolicy;
